const FILE_DRAG_EVENTS = [
  'dragenter',
  'dragover',
  'dragleave',
  'drop',
] as const;

const installed = new WeakSet<EventTarget>();

function dragCarriesFiles(event: Event): boolean {
  const transfer = (event as DragEvent).dataTransfer;
  if (!transfer) {
    return false;
  }
  return Array.from(transfer.types).includes('Files');
}

/**
 * Stop external file drags that hit the overlay host from continuing into the
 * app tree.
 *
 * The host is painted above `#root`, so whatever overlay is open already
 * covers the UI under the pointer. React still retargets a portal event
 * through the fiber parent, and the covered surface (the chat drop zone,
 * among others) would accept the file. Every overlay portals into this host,
 * so the block lives here rather than on one popup. `drop` is also cancelled
 * so the page does not navigate to the dropped file.
 *
 * Drags whose target is outside the host are untouched.
 */
export function blockFileDragThroughOverlayHost(host: EventTarget): () => void {
  if (installed.has(host)) {
    return () => {};
  }
  installed.add(host);
  const block = (event: Event): void => {
    if (!dragCarriesFiles(event)) {
      return;
    }
    event.stopImmediatePropagation();
    if (event.type === 'drop') {
      event.preventDefault();
    }
  };
  for (const type of FILE_DRAG_EVENTS) {
    host.addEventListener(type, block, true);
  }
  return () => {
    installed.delete(host);
    for (const type of FILE_DRAG_EVENTS) {
      host.removeEventListener(type, block, true);
    }
  };
}
